import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { authAPI, UserResponse } from '@/api/auth'

interface User {
  id: string
  email: string
  fullName: string
  role: string
  orgId: string
  orgCode: string
}

interface AuthState {
  token: string | null
  refreshToken: string | null
  user: User | null

  /** Store tokens received from OIDC callback redirect. */
  setTokens: (token: string, refreshToken: string) => void

  /** Fetch user profile from /auth/me and store it. */
  fetchUser: () => Promise<void>

  /** Logout: revoke tokens, clear state, redirect to Zitadel end_session. */
  logout: () => Promise<void>

  /** Refresh the platform access token using the refresh token. */
  refreshAccessToken: () => Promise<void>

  /** Clear auth state without API call (used on 401 fallback). */
  clearAuth: () => void
}

function mapUser(u: UserResponse): User {
  return {
    id: u.id,
    email: u.email,
    fullName: u.full_name,
    role: u.role,
    orgId: u.org_id,
    orgCode: u.org_code,
  }
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      refreshToken: null,
      user: null,

      setTokens: (token, refreshToken) => {
        set({ token, refreshToken })
      },

      fetchUser: async () => {
        const me = await authAPI.getMe()
        set({ user: mapUser(me) })
      },

      logout: async () => {
        let logoutUrl: string | null = null
        try {
          const response = await authAPI.logout()
          logoutUrl = response.logout_url
        } catch (error) {
          console.error('Logout API error:', error)
        } finally {
          set({ token: null, refreshToken: null, user: null })
        }

        if (logoutUrl) {
          window.location.href = logoutUrl
        } else {
          window.location.href = '/login'
        }
      },

      refreshAccessToken: async () => {
        const { refreshToken } = get()
        if (!refreshToken) {
          throw new Error('No refresh token available')
        }
        const response = await authAPI.refresh(refreshToken)
        set({ token: response.access_token })
      },

      clearAuth: () => {
        set({ token: null, refreshToken: null, user: null })
      },
    }),
    {
      name: 'medcifra-auth',
      partialize: (state) => ({
        token: state.token,
        refreshToken: state.refreshToken,
        user: state.user,
      }),
    }
  )
)
